import { getAppConfig } from '../config';
import type {
  InterStoreTransferAnalysisRequest,
  InterStoreTransferAnalysisResponse,
} from '@/lib/types';

// 後方互換性のため、型を再エクスポート
export type {
  InterStoreTransferAnalysisRequest,
  InterStoreTransferAnalysisResponse,
} from '@/lib/types';

/**
 * 絶対URLを構築するヘルパー関数
 */
function buildAbsoluteUrl(baseUrl: string, path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  // baseUrlが相対パス（/で始まる）の場合、現在のoriginと組み合わせる
  if (baseUrl.startsWith('/')) {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
    const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const pathWithSlash = path.startsWith('/') ? path : `/${path}`;
    return `${origin}${base}${pathWithSlash}`;
  }

  // baseUrlが絶対URLの場合は従来の処理
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const pathWithSlash = path.startsWith('/') ? path : `/${path}`;

  return `${base}${pathWithSlash}`;
}

/**
 * 店間移動効果検証の分析を実行（分析実行用）
 */
export async function analyzeInterStoreTransfer(
  accountId: string,
  request: InterStoreTransferAnalysisRequest,
  authHeaders?: Record<string, string>,
): Promise<InterStoreTransferAnalysisResponse> {
  const formData = new FormData();

  // ファイルをFormDataに追加
  formData.append('transfer_order_file', request.transferOrderFile);
  formData.append('inventory_date', request.inventoryDate);
  formData.append('start_date', request.startDate);
  formData.append('end_date', request.endDate);
  formData.append('execution_rate_start_date', request.executionRateStartDate);
  formData.append('execution_rate_end_date', request.executionRateEndDate);

  if (request.transferCost !== undefined) {
    formData.append('transfer_cost', request.transferCost.toString());
  }

  const config = getAppConfig();

  // API Base URLの検証
  if (!config.apiBaseUrl) {
    console.error('API設定エラー:', {
      env: config.env,
      apiBaseUrl: config.apiBaseUrl,
      envVars: {
        VITE_APP_ENV: import.meta.env.VITE_APP_ENV,
        VITE_BACKEND_API_URL: import.meta.env.VITE_BACKEND_API_URL,
      },
    });
    throw new Error(
      `API Base URLが設定されていません。環境設定を確認してください。現在の環境: ${config.env}`,
    );
  }

  let url: URL;
  try {
    const absoluteUrl = buildAbsoluteUrl(
      config.apiBaseUrl,
      `/${accountId}/inter-store-transfer-analysis`,
    );
    url = new URL(absoluteUrl);
    console.log('🔗 API URL構築:', { baseUrl: config.apiBaseUrl, absoluteUrl });
  } catch (error) {
    console.error('URL構築エラー:', {
      apiBaseUrl: config.apiBaseUrl,
      accountId,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    throw new Error(`不正なAPI Base URL: ${config.apiBaseUrl}`);
  }

  const headers: Record<string, string> = {
    ...authHeaders,
  };

  // Content-TypeはFormDataの場合、ブラウザが自動設定するため削除
  delete headers['Content-Type'];

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`店間移動効果検証分析の実行に失敗しました: ${response.status}`);
  }

  return response.json();
}
